'use client';

import { useRef, useEffect, type ReactNode } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useUpdateEntity } from '@/lib/hooks/useEntities';
import type { ApiEntity } from '@/lib/api/types';
import { EntityForm, type EntityFormRef } from './EntityForm';
import { applyApiFieldError } from '@/lib/hooks/useApiFormError';
import { useSession } from '@/lib/auth-client';

/**
 * Interface for custom form components used with EditEntityDialog.
 * The custom component must expose submit via ref.
 */
export interface CustomEntityFormRef {
  submit: () => void;
}
interface EditEntityDialogBaseProps {
  isOpen: boolean;
  onClose: () => void;
  entity: ApiEntity;
  title?: string;
  explainer?: React.ReactNode;
  maxWidth?: string;
}
interface EditEntityDialogDefaultProps extends EditEntityDialogBaseProps {
  /** When not provided, uses the default EntityForm */
  customForm?: undefined;
}
interface EditEntityDialogCustomProps extends EditEntityDialogBaseProps {
  /**
   * Render a custom form instead of EntityForm.
   * Receives entity, onSubmit callback, isPending state, and a ref to attach.
   */
  customForm: (props: {
    entity: ApiEntity;
    onSubmit: (data: Partial<ApiEntity>) => void;
    isPending: boolean;
    ref: React.Ref<CustomEntityFormRef>;
  }) => ReactNode;
}
export type EditEntityDialogProps = EditEntityDialogDefaultProps | EditEntityDialogCustomProps;
export function EditEntityDialog({
  isOpen,
  onClose,
  entity,
  title,
  explainer,
  maxWidth,
  ...rest
}: EditEntityDialogProps) {
  const updateEntity = useUpdateEntity();
  const formRef = useRef<EntityFormRef>(null);
  const customFormRef = useRef<CustomEntityFormRef>(null);
  const {
    data: sessionData
  } = useSession();
  const isPlatformAdmin = !!sessionData?.user?.isAdmin;
  const hasCustomForm = 'customForm' in rest && rest.customForm !== undefined;

  // Ensure body scroll is restored when dialog closes
  useEffect(() => {
    if (!isOpen) {
      // Force restore scroll immediately and after delays
      const restore = () => {
        document.body.style.overflow = '';
        document.body.style.pointerEvents = '';
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('pointer-events');

        // Also check for any portal containers that might be blocking
        const portals = document.querySelectorAll('[data-radix-portal]');
        portals.forEach(portal => {
          if (portal instanceof HTMLElement) {
            portal.style.pointerEvents = '';
          }
        });
      };

      // Restore immediately
      restore();

      // Restore after Radix cleanup
      const timer1 = setTimeout(restore, 50);
      const timer2 = setTimeout(restore, 150);
      const timer3 = setTimeout(restore, 300);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [isOpen]);
  const handleSave = () => {
    if (hasCustomForm) {
      customFormRef.current?.submit();
    } else {
      formRef.current?.submit();
    }
  };

  /** Direct update handler for custom forms — accepts partial entity data to PATCH */
  const handleCustomFormSubmit = async (data: Partial<ApiEntity>) => {
    try {
      updateEntity.mutate({
        id: String(entity.id),
        data
      }, {
        onSuccess: () => {
          toast.success('Updated successfully');
          onClose();
        },
        onError: (error_0: any) => {
          toast.error(error_0?.message || 'Failed to update');
        }
      });
    } catch (error: any) {
      console.error('Failed to update entity:', error);
      toast.error(error?.message || 'Failed to update');
    }
  };
  const handleFormSubmit = async (formData: any) => {
    try {
      // Build links array from social media URLs
      const links = [];
      if (formData.instagram_url?.trim()) {
        links.push({
          platform: 'instagram',
          url: formData.instagram_url.trim()
        });
      }
      if (formData.linkedin_url?.trim()) {
        links.push({
          platform: 'linkedin',
          url: formData.linkedin_url.trim()
        });
      }
      if (formData.facebook_url?.trim()) {
        links.push({
          platform: 'facebook',
          url: formData.facebook_url.trim()
        });
      }
      if (formData.tiktok_url?.trim()) {
        links.push({
          platform: 'tiktok',
          url: formData.tiktok_url.trim()
        });
      }

      // Merge with existing data
      const updatedData: any = {
        ...(entity.data || {})
      };

      // Contact details (stored in entity.data.contactDetails)
      updatedData.contactDetails = {
        name: formData.contactDetailsName?.trim() || undefined,
        email: formData.contactDetailsEmail?.trim() || undefined,
        phone: formData.contactDetailsPhone?.trim() || undefined
      };
      if (links.length > 0) {
        updatedData.links = links;
      }
      if (formData.roles && formData.roles.length > 0) {
        updatedData.roles = formData.roles;
      }
      if (formData.capabilities) {
        updatedData.capabilities = formData.capabilities;
      }
      if (formData.specialisations) {
        updatedData.specialisations = formData.specialisations;
      }
      if (formData.channelExpertise) {
        updatedData.channelExpertise = formData.channelExpertise;
      }
      if (formData.geoPresence) {
        updatedData.geoPresence = formData.geoPresence;
      }
      if (formData.geoExpertise) {
        updatedData.geoExpertise = formData.geoExpertise;
      }
      if (formData.galleryImages) {
        updatedData.gallery = {
          images: formData.galleryImages
        };
      }
      if (formData.showInitiatives !== undefined) {
        const existingMainPage = entity.data?.main_page && typeof entity.data.main_page === 'object' ? entity.data.main_page : {};
        updatedData.main_page = {
          ...existingMainPage,
          showInitiatives: formData.showInitiatives,
          showItems: formData.showItems,
          showEnquiryForm: formData.showEnquiryForm ?? false,
          showContactDetails: formData.showContactDetails ?? false,
          showContentFeed: formData.showContentFeed ?? false,
          contentFeedTitle: formData.contentFeedTitle || '',
          sidebarPosition: formData.sidebarPosition || 'right'
        };
      }
      if (formData.initiativesSectionTitle !== undefined || formData.initiativesDescription !== undefined) {
        const existingInitiatives = entity.data?.initiatives && typeof entity.data.initiatives === 'object' ? entity.data.initiatives : {};
        updatedData.initiatives = {
          ...existingInitiatives,
          sectionTitle: formData.initiativesSectionTitle || '',
          description: formData.initiativesDescription || '',
          allowPartnerProposals: formData.allowPartnerProposals ?? false
        };
      }
      if (formData.itemsSectionTitle !== undefined) {
        const existingItems = entity.data?.items && typeof entity.data.items === 'object' ? entity.data.items : {};
        updatedData.items = {
          ...existingItems,
          sectionTitle: formData.itemsSectionTitle || ''
        };
      }
      if (formData.trade_enabled !== undefined) {
        const existingTrade = entity.data?.trade && typeof entity.data.trade === 'object' ? entity.data.trade : {};
        updatedData.trade = {
          ...existingTrade,
          enabled: formData.trade_enabled
        };
      }
      if (formData.hosts_activations !== undefined || formData.runs_activations !== undefined) {
        const existingActivations = entity.data?.activations && typeof entity.data.activations === 'object' ? entity.data.activations as any : {};
        updatedData.activations = {
          ...existingActivations,
          hosts: formData.hosts_activations ?? (existingActivations as any).hosts ?? false,
          runs: formData.runs_activations ?? (existingActivations as any).runs ?? false
        };
      }
      if (formData.base_region !== undefined || formData.regions_served !== undefined) {
        const existingReporting = entity.data?.reporting && typeof entity.data.reporting === 'object' ? entity.data.reporting : {};
        updatedData.reporting = {
          ...existingReporting,
          base_region: formData.base_region,
          regions_served: formData.regions_served
        };
      }
      if (formData.exportsTo && formData.exportsTo.length > 0) {
        updatedData.exports_to = formData.exportsTo;
      } else if (formData.exportsTo && formData.exportsTo.length === 0) {
        // Explicitly clear exports_to if empty array is provided
        updatedData.exports_to = [];
      }

      // Handle address_1: store place detail in data, backend syncs geography column
      if (formData.address_1 !== undefined) {
        updatedData.address_1 = formData.address_1;
      }

      // Engagement settings
      updatedData.allowReactions = formData.allowReactions !== false;
      updatedData.allowComments = formData.allowComments !== false;

      // Themes
      if (formData.themes !== undefined) {
        updatedData.themes = formData.themes;
      }
      if (formData.themeFeatured !== undefined) {
        updatedData.themeFeatured = formData.themeFeatured;
      }
      if (formData.featuredPartner !== undefined) {
        updatedData.featuredPartner = formData.featuredPartner;
      }

      // Hubs
      if (formData.hubs !== undefined) {
        updatedData.hubs = formData.hubs;
      }

      // Parse tags from comma-separated string
      const tags = formData.tags ? formData.tags.split(',').map((t: string) => t.trim()).filter((t_0: string) => t_0.length > 0) : [];
      updateEntity.mutate({
        id: String(entity.id),
        data: {
          name_1: formData.name_1.trim(),
          slug: formData.slug?.trim() || null,
          description: formData.description?.trim() || null,
          image: formData.image || null,
          website: formData.website?.trim() || null,
          primary_email: formData.primary_email?.trim() || null,
          primary_phone: formData.primary_phone?.trim() || null,
          visibility: formData.visibility || 'visible',
          tags,
          data: updatedData,
          ...(isPlatformAdmin && formData.notes !== undefined && {
            systemData: {
              notes: formData.notes?.trim() || ''
            }
          })
        }
      }, {
        onSuccess: () => {
          toast.success('Entity updated successfully');
          onClose();
        },
        onError: (error_2: any) => {
          const handled = applyApiFieldError(error_2, formRef.current?.setError);
          if (!handled) {
            toast.error(error_2?.message || 'Failed to update entity');
          }
        }
      });
    } catch (error_1: any) {
      console.error('Failed to update entity:', error_1);
      toast.error(error_1?.message || 'Failed to update entity');
    }
  };
  return <Dialog open={isOpen} onOpenChange={onClose} modal={true}>
      <DialogContent className={`${maxWidth || 'sm:max-w-[800px]'} flex flex-col max-h-[90vh] p-0`} onCloseAutoFocus={() => {
      // Ensure scroll is restored after dialog closes
      setTimeout(() => {
        document.body.style.overflow = '';
        document.body.style.pointerEvents = '';
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('pointer-events');
      }, 0);
    }} onInteractOutside={e => {
      // Allow interaction with dropdown menus
      const target = e.target as Element;
      if (target.closest('[data-radix-dropdown-menu-content]')) {
        e.preventDefault();
      }
    }}>
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-xl font-bold text-orange-500">
            {title || `Edit ${entity.type}`}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {explainer && <div className="mb-4">{explainer}</div>}
          {hasCustomForm ? (rest as EditEntityDialogCustomProps).customForm({
          entity,
          onSubmit: handleCustomFormSubmit,
          isPending: updateEntity.isPending,
          ref: customFormRef
        }) : <EntityForm ref={formRef} entity={entity} onSubmit={handleFormSubmit} showRoles isPlatformAdmin={isPlatformAdmin} />}
        </div>

        <DialogFooter className="px-6 py-4 border-t">
          <Button variant="ghost" onClick={onClose} disabled={updateEntity.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} className="bg-orange-500 hover:bg-orange-600 text-white" disabled={updateEntity.isPending}>
            {updateEntity.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}